import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2 } from 'lucide-react';
import { AuthShell } from '../components/shell/AuthShell';

/**
 * Forgot-password request page. Intentionally always shows the same
 * "check your email" confirmation regardless of whether the email
 * exists — matches the backend's non-enumeration design.
 */
export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await fetch('/admin/password/request-reset', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.status === 429) {
        const b = await res.json().catch(() => null) as { retry_after_seconds?: number } | null;
        throw new Error(`Too many requests — try again in ${b?.retry_after_seconds ?? 60}s`);
      }
      if (!res.ok && res.status !== 202) {
        throw new Error(`status ${res.status}`);
      }
      setSubmitted(true);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <AuthShell
        icon={CheckCircle2}
        title="Check your email"
        subtitle={
          <>
            If an admin account exists for <code>{email}</code>, we've sent a link to reset your password.
            The link expires in 30 minutes.
          </>
        }
        footer={
          <Link to="/signin" style={{ color: 'var(--accent-text)' }}>← Back to sign in</Link>
        }
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8,
            padding: '14px 16px',
            borderRadius: 12,
            background: 'rgba(52, 211, 153, 0.10)',
            border: '1px solid rgba(52, 211, 153, 0.35)',
            color: 'var(--success)',
            fontSize: 13,
          }}
        >
          <Mail width={14} height={14} />
          Reset link delivered (or quietly absorbed if the email is unknown).
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      icon={Mail}
      title="Reset password"
      subtitle="Enter the email on your admin account. We'll send you a link to choose a new password."
      footer={
        <Link to="/signin" style={{ color: 'var(--text-muted)' }}>← Back to sign in</Link>
      }
    >
      <form onSubmit={(e) => void submit(e)} style={{ display: 'grid', gap: 12, marginTop: 4 }}>
        <input
          type="email" value={email} required autoFocus
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourcompany.com"
          autoComplete="email"
          disabled={busy}
        />
        <button type="submit" className="primary" disabled={busy || !email}>
          {busy ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
      {err && <p className="err" style={{ marginTop: 14 }}>{err}</p>}
    </AuthShell>
  );
}
